import { Layout } from '@/components/Layout';
import { addMetadata, setUser } from '@/config/bugsnagHelpers';
import { getUserInfo, selectAuthUser } from '@/features/auth';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { Center, Spinner } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { EmailVerificationClaim } from 'supertokens-web-js/recipe/emailverification';
import MultiFactorAuth from 'supertokens-web-js/recipe/multifactorauth';
import Session from 'supertokens-web-js/recipe/session';
import { AuthContext } from './AuthContext';

export interface AuthProviderProps {
  children?: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuthUser);
  const router = useRouter();

  const [isLoading, setIsLoading] = useState(true);
  const [isUserVerified, setIsUserVerified] = useState(true);
  const hasInitialized = useRef(false);

  const publicRoutes = ['/', '/register', '/forgot', '/reset-password', '/two-factor'];
  const isPublicRoute = publicRoutes.includes(router.pathname);

  const checkSession = async () => {
    try {
      const isSession = await Session.doesSessionExist();

      if (isSession) {
        try {
          const mfaInfo = await MultiFactorAuth.resyncSessionAndFetchMFAInfo();

          if (mfaInfo.factors.next.length > 0) {
            if (router.pathname !== '/two-factor') {
              router.push('/two-factor');
            }
            return;
          }
        } catch (mfaError) {}

        if (!user) {
          await dispatch(getUserInfo()).unwrap();
        }

        const validationErrors = await Session.validateClaims();
        const emailVerificationError = validationErrors.find(
          (err) => err.id === EmailVerificationClaim.id,
        );

        if (emailVerificationError) {
          setIsUserVerified(false);
          if (!isPublicRoute) {
            router.push('/');
          }
        } else {
          setIsUserVerified(true);
        }
      } else {
        if (!isPublicRoute) {
          router.push('/');
        }
      }
    } catch (error) {
      if (!isPublicRoute) {
        router.push('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      checkSession();
    }
  }, []);

  useEffect(() => {
    if (user) {
      const safeUser: any = user;
      const userId = safeUser.id;
      const email = safeUser.emails[0];

      if (userId) {
        setUser(userId, email || null);
        addMetadata('user', {
          id: userId,
          email,
          isAdmin: safeUser.isAdmin,
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (!isLoading && user && isUserVerified && isPublicRoute && router.pathname !== '/results') {
      router.push('/results');
    }
  }, [user, isUserVerified, isLoading, isPublicRoute, router]);

  if (isLoading) {
    return (
      <Layout>
        <Center>
          <Spinner size="xl" />
        </Center>
      </Layout>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, isUserVerified }}>
      {children}
    </AuthContext.Provider>
  );
};
